import { Clock3, X } from 'lucide-react';
import { useMemo, useState } from 'react';

interface SnoozeDialogProps {
  count: number;
  initialUntil?: number | null;
  pending?: boolean;
  onClose: () => void;
  onConfirm: (until: number) => void;
  onUnsnooze?: () => void;
}

export function SnoozeDialog({
  count,
  initialUntil,
  pending = false,
  onClose,
  onConfirm,
  onUnsnooze,
}: SnoozeDialogProps) {
  const now = useMemo(() => new Date(), []);
  const presets = useMemo(() => snoozePresets(now), [now]);
  const initial = new Date(initialUntil && initialUntil > now.getTime()
    ? initialUntil
    : presets[1]!.at);
  const [date, setDate] = useState(() => localDateInput(initial));
  const [time, setTime] = useState(() => localTimeInput(initial));
  const customAt = localDateTime(date, time);
  const validCustom = customAt !== null && customAt > Date.now() + 30_000;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/45 p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !pending) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="snooze-title"
        className="w-full max-w-md rounded-2xl border border-line bg-surface p-5 shadow-2xl"
      >
        <header className="flex items-start gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent">
            <Clock3 className="size-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="snooze-title" className="text-lg font-semibold">
              Snooze {count === 1 ? 'conversation' : `${count} conversations`}
            </h2>
            <p className="mt-1 text-sm text-ink-muted">
              Hide {count === 1 ? 'it' : 'them'} from Wintermute inboxes until the selected time.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            className="rounded-full p-2 text-ink-muted hover:bg-hover disabled:opacity-50"
            aria-label="Close snooze picker"
          >
            <X className="size-4" />
          </button>
        </header>

        <div className="mt-5 grid gap-2 sm:grid-cols-2">
          {presets.map((preset) => (
            <button
              key={preset.label}
              type="button"
              onClick={() => onConfirm(preset.at)}
              disabled={pending}
              className="flex items-center justify-between rounded-xl border border-line px-3 py-2.5 text-left text-sm hover:border-accent hover:bg-accent-soft disabled:opacity-50"
            >
              <span className="font-medium">{preset.label}</span>
              <span className="text-xs text-ink-muted">{formatPresetTime(preset.at, now)}</span>
            </button>
          ))}
        </div>

        <fieldset className="mt-5 rounded-xl border border-line p-3">
          <legend className="px-1 text-xs font-medium text-ink-muted">Custom date and time</legend>
          <div className="grid grid-cols-[1fr_8rem] gap-2">
            <input
              type="date"
              value={date}
              min={localDateInput(now)}
              onChange={(event) => setDate(event.target.value)}
              disabled={pending}
              className="min-w-0 rounded-lg border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-accent"
              aria-label="Snooze date"
            />
            <input
              type="time"
              value={time}
              step={300}
              onChange={(event) => setTime(event.target.value)}
              disabled={pending}
              className="min-w-0 rounded-lg border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-accent"
              aria-label="Snooze time"
            />
          </div>
          <button
            type="button"
            onClick={() => customAt !== null && onConfirm(customAt)}
            disabled={pending || !validCustom}
            className="mt-3 w-full rounded-full bg-accent px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 dark:text-black"
          >
            Snooze until custom time
          </button>
        </fieldset>

        {onUnsnooze ? (
          <button
            type="button"
            onClick={onUnsnooze}
            disabled={pending}
            className="mt-4 w-full rounded-full border border-line px-4 py-2 text-sm font-medium hover:bg-hover disabled:opacity-50"
          >
            Return to inbox now
          </button>
        ) : null}
      </section>
    </div>
  );
}

export function snoozePresets(now: Date): Array<{ label: string; at: number }> {
  const todayEvening = atLocalTime(now, 18, 0);
  const laterToday = todayEvening.getTime() > now.getTime() + 30 * 60_000
    ? todayEvening
    : atLocalTime(addDays(now, 1), 8, 0);
  const tomorrow = atLocalTime(addDays(now, 1), 8, 0);
  const daysUntilMonday = ((8 - now.getDay()) % 7) || 7;
  const nextMonday = atLocalTime(addDays(now, daysUntilMonday), 8, 0);
  const daysUntilSaturday = ((6 - now.getDay() + 7) % 7) || 7;
  const weekend = atLocalTime(addDays(now, daysUntilSaturday), 9, 0);
  return [
    { label: laterToday === todayEvening ? 'Later today' : 'Tomorrow morning', at: laterToday.getTime() },
    { label: 'Tomorrow morning', at: tomorrow.getTime() },
    { label: 'Next Monday', at: nextMonday.getTime() },
    { label: 'This weekend', at: weekend.getTime() },
  ].filter((preset, index, all) => all.findIndex((candidate) => candidate.at === preset.at) === index);
}

function addDays(value: Date, days: number): Date {
  const next = new Date(value);
  next.setDate(next.getDate() + days);
  return next;
}

function atLocalTime(value: Date, hours: number, minutes: number): Date {
  const next = new Date(value);
  next.setHours(hours, minutes, 0, 0);
  return next;
}

function localDateInput(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function localTimeInput(value: Date): string {
  return `${String(value.getHours()).padStart(2, '0')}:${String(value.getMinutes()).padStart(2, '0')}`;
}

function localDateTime(date: string, time: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return null;
  const parsed = new Date(`${date}T${time}:00`);
  return Number.isFinite(parsed.getTime()) ? parsed.getTime() : null;
}

function formatPresetTime(at: number, now: Date): string {
  const value = new Date(at);
  const time = value.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return value.toDateString() === now.toDateString()
    ? time
    : `${value.toLocaleDateString([], { weekday: 'short' })} ${time}`;
}
